import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import { useMatchRoute } from '@mint/routing';

import type { DataFilePreview } from '../../api/types.js';
import { useDataFileRouteApi } from '../../hooks/useImportHubRouteApi.js';
import { useDataFilePreview } from '../../queries/index.js';
import {
  createClientStepsConfig,
  getDataFileClientStep,
} from '../../utils/index.js';

export const useSyncDataFileRoute = (
  dataFileId: string,
  dataFilePreview: DataFilePreview,
) => {
  const matchRoute = useMatchRoute();
  const navigate = useDataFileRouteApi().useNavigate();
  const clientStepsConfig = useDataFileClientStepsConfig(dataFileId);

  const redirectIfNeeded = useCallback(
    async (dataFilePreview: DataFilePreview) => {
      const isValidRoute = getIsValidRoute({
        clientStepsConfig,
        dataFilePreview,
        matchRoute,
      });

      if (!isValidRoute) {
        const clientStepConfig = getClientStepConfig(
          clientStepsConfig,
          dataFilePreview,
        );
        await navigate({
          ...clientStepConfig.link,
          replace: true,
        });
      }
    },
    [navigate, clientStepsConfig, matchRoute],
  );

  const syncRoute = useEffectEvent(() => {
    redirectIfNeeded(dataFilePreview);
  });

  const routeStateKey = [
    dataFilePreview.currentStep,
    dataFilePreview.status,
    dataFilePreview.isFileUploaded,
    dataFilePreview.templateSheetsMismatched,
    dataFilePreview.templateHeadersMismatched,
  ].join('|');

  useEffect(() => {
    syncRoute();
  }, [routeStateKey]);
};

const useDataFileClientStepsConfig = (dataFileId: string) => {
  const { t } = useTranslation('@myn/import-hub');
  const ff = useFlags('@myn/secured', ['externalDataFeature']);
  const {
    data: { granularityLevel },
  } = useDataFilePreview(dataFileId);

  return useMemo(
    () =>
      createClientStepsConfig({
        t,
        granularityLevel,
        isExternalData: ff.externalDataFeature.enabled,
      }),
    [t, granularityLevel, ff.externalDataFeature.enabled],
  );
};

type ClientStepsConfig = ReturnType<typeof useDataFileClientStepsConfig>;
type MatchRoute = ReturnType<typeof useMatchRoute>;

const getClientStepConfig = (
  clientStepsConfig: ClientStepsConfig,
  dataFilePreview: DataFilePreview,
) => {
  const currentClientStep = getDataFileClientStep(dataFilePreview);
  const currentClientStepConfig = clientStepsConfig[currentClientStep];

  return currentClientStepConfig;
};

const getIsValidRoute = ({
  clientStepsConfig,
  dataFilePreview,
  matchRoute,
}: {
  clientStepsConfig: ClientStepsConfig;
  dataFilePreview: DataFilePreview;
  matchRoute: MatchRoute;
}) => {
  const clientStepConfig = getClientStepConfig(
    clientStepsConfig,
    dataFilePreview,
  );
  const isValidRoute = matchRoute({
    to: clientStepConfig.link.to,
    fuzzy: true,
  });

  return isValidRoute;
};
